import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Newspaper, Calendar, ArrowLeft, Tag } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { formatDateFr } from "@/lib/formatters";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two modes:
 *   - List mode (route `/actualites`)   : grid of cards
 *   - Article mode (route `/actualites/:slug`) : single article view
 *
 * The same file handles both so we can share the layout/header.
 */
export default function ActualitesPage() {
  const { slug } = useParams();
  return slug ? <ArticleView slug={slug} /> : <ListView />;
}

function ListView() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/news?limit=30`);
        setArticles(r.data || []);
      } catch { setArticles([]); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <>
      <Navbar />
      <main className="bg-[#FAFAFA] min-h-screen pb-20" data-testid="actualites-page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#662483] to-[#0069B4] py-16 px-4">
          <div className="max-w-6xl mx-auto text-white">
            <div className="flex items-center gap-3 mb-4">
              <Newspaper className="w-8 h-8" />
              <span className="text-sm uppercase tracking-widest font-semibold opacity-80">FR INTERIM</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-3">Actualités</h1>
            <p className="text-lg opacity-90 max-w-2xl">
              Salons emploi, forums, événements logistique et transport — restez informé de toutes nos initiatives terrain.
            </p>
          </div>
        </section>

        {/* List */}
        <section className="max-w-6xl mx-auto px-4 py-12">
          {loading ? (
            <p className="text-center text-gray-500">Chargement…</p>
          ) : articles.length === 0 ? (
            <div className="text-center text-gray-500 py-20">
              <Newspaper className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>Aucune actualité publiée pour le moment.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="actualites-grid">
              {articles.map((a, idx) => (
                <motion.article
                  key={a.article_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white border border-gray-200 hover:shadow-lg transition-shadow group"
                  data-testid={`actualite-card-${a.slug}`}
                >
                  <Link to={`/actualites/${a.slug}`} className="block">
                    {a.cover_image ? (
                      <div className="aspect-[16/9] bg-gradient-to-br from-[#662483] to-[#0069B4] overflow-hidden">
                        <img
                          src={a.cover_image.startsWith("http") ? a.cover_image : `${process.env.REACT_APP_BACKEND_URL}${a.cover_image}`}
                          alt={a.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[16/9] bg-gradient-to-br from-[#662483] to-[#0069B4] flex items-center justify-center">
                        <Newspaper className="w-12 h-12 text-white/40" />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-[11px] text-gray-500 mb-2">
                        <Calendar className="w-3 h-3" />
                        <time>{formatDateFr(a.published_at || a.created_at)}</time>
                        {a.tags?.[0] && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="bg-[#F0E9F3] text-[#662483] px-2 py-0.5 font-semibold uppercase tracking-wider">{a.tags[0]}</span>
                          </>
                        )}
                      </div>
                      <h2 className="text-lg font-bold text-[#3F3F3F] group-hover:text-[#662483] transition-colors line-clamp-2">{a.title}</h2>
                      {a.excerpt && <p className="text-sm text-gray-600 mt-2 line-clamp-3">{a.excerpt}</p>}
                      <p className="mt-4 text-[#662483] text-xs font-bold uppercase tracking-widest group-hover:translate-x-1 transition-transform inline-block">Lire la suite →</p>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}

function ArticleView({ slug }) {
  const [art, setArt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/news/${slug}`);
        setArt(r.data);
      } catch { setArt(null); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center text-gray-500">Chargement…</main>
        <Footer />
      </>
    );
  }
  if (!art) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-20">
          <Newspaper className="w-12 h-12 text-gray-300 mb-4" />
          <p className="text-gray-500 mb-6">Article introuvable.</p>
          <Link to="/actualites" className="text-[#662483] hover:underline font-semibold inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Retour aux actualités
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="bg-white" data-testid={`actualite-${art.slug}`}>
        {/* Cover — bounded height + object-contain so portrait posters
            aren't cropped, with a soft brand-colored letterboxing. */}
        {art.cover_image && (
          <div className="bg-[#F0E9F3] border-b border-gray-100">
            <div className="max-w-4xl mx-auto flex items-center justify-center">
              <img
                src={art.cover_image.startsWith("http") ? art.cover_image : `${process.env.REACT_APP_BACKEND_URL}${art.cover_image}`}
                alt={art.title}
                className="w-auto max-w-full max-h-[480px] object-contain"
              />
            </div>
          </div>
        )}

        {/* Article */}
        <article className="max-w-3xl mx-auto px-4 py-12">
          <Link to="/actualites" className="text-[#662483] hover:underline text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="w-3 h-3" /> Toutes les actualités
          </Link>
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3 flex-wrap">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDateFr(art.published_at || art.created_at)}</span>
            {art.tags?.map((t) => (
              <span key={t} className="bg-[#F0E9F3] text-[#662483] px-2 py-0.5 font-semibold uppercase tracking-wider inline-flex items-center gap-1">
                <Tag className="w-3 h-3" /> {t}
              </span>
            ))}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#3F3F3F] leading-tight mb-4">{art.title}</h1>
          {art.excerpt && <p className="text-lg text-gray-600 mb-8 leading-relaxed border-l-4 border-[#662483] pl-4">{art.excerpt}</p>}
          <div
            className="prose prose-sm sm:prose-base max-w-none prose-headings:text-[#3F3F3F] prose-headings:font-bold prose-a:text-[#0069B4] prose-strong:text-[#3F3F3F]"
            dangerouslySetInnerHTML={{ __html: art.body || "" }}
            data-testid="actualite-body"
          />
          <div className="mt-12 pt-6 border-t border-gray-200 text-xs text-gray-400">
            {art.views || 0} lecture(s)
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
